import type { JSX } from "react";

import type {
  ClipIcon,
  ClipMatchSummary,
  ClipPlayerRow,
  ClipStat,
  ClipTeam,
  ClipTone
} from "@shared/clipSidecar";
import { ClipAssetTip } from "@/clip/ClipAssetTip";
import { ClipRiotIdLabel } from "@/clip/ClipRiotIdLabel";
import { Tip } from "@/components/InfoTip";
import { cn } from "@/lib/utils";

/**
 * Match details, rendered straight from the sidecar.
 *
 * Champions, ranks, and icon URLs are finished display strings from the app.
 * League item / spell / rune tips are tipIds only — the website fetches Data
 * Dragon on hover. Layout mirrors the app's MatchDetails column for column.
 */

// --- shared pieces -----------------------------------------------------------

const TONE_CLASS: Record<ClipTone, string> = {
  default: "text-foreground",
  good: "text-sky-400",
  great: "text-amber-400",
  bad: "text-rose-400"
};

/**
 * Older sidecars labeled 0/0/0 as Perfect; show 0.00 KDA instead (app rule).
 */
const kdaRatioSub = (cell: ClipStat | undefined): { text: string; tone: ClipTone } | null => {
  if (!cell?.sub) return null;
  const perfect = /^perfect\b/i.test(cell.sub.trim());
  const parts = cell.value.match(/^(\d+)\s*\/\s*(\d+)\s*\/\s*(\d+)$/);
  if (perfect && parts) {
    const kills = Number(parts[1]);
    const deaths = Number(parts[2]);
    const assists = Number(parts[3]);
    if (deaths === 0 && kills + assists === 0) {
      return { text: "0.00 KDA", tone: "default" };
    }
  }
  return { text: cell.sub, tone: cell.tone ?? "default" };
};

/**
 * Fixed square so a missing or slow icon never reflows the row.
 *
 * An icon with a tipId gets the League asset hover card (fetched on open);
 * one with only a label gets a plain tooltip.
 *
 * `cropBorder` zooms past Data Dragon's baked dark rim (champion portraits) —
 * same treatment as the app's AssetIcon.
 */
const Icon = ({
  icon,
  ddragonVersion,
  className,
  rounded = "rounded-sm",
  cropBorder = false
}: {
  icon: ClipIcon | null | undefined;
  ddragonVersion?: string | null;
  className: string;
  rounded?: string;
  cropBorder?: boolean;
}): JSX.Element => {
  if (!icon) {
    return <span className={cn("shrink-0 bg-muted/40", rounded, className)} />;
  }
  const image = (
    <span className={cn("block shrink-0 overflow-hidden bg-muted/40", rounded, className)}>
      <img
        src={icon.url}
        alt={icon.alt ?? ""}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        className={cn("size-full cursor-help object-cover", cropBorder && "scale-[1.12]")}
      />
    </span>
  );
  if (icon.tipId)
    return (
      <ClipAssetTip icon={icon} ddragonVersion={ddragonVersion}>
        {image}
      </ClipAssetTip>
    );
  if (!icon.alt) return image;
  return (
    <Tip label={icon.alt} side="top" showCaret>
      {image}
    </Tip>
  );
};

// --- stat tiles --------------------------------------------------------------

/**
 * Tile grid widths, spelled out because Tailwind only ships classes it can see
 * in the source — a computed `lg:grid-cols-${n}` would never be generated.
 */
const TILE_GRID: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-4",
  5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
  6: "grid-cols-3 sm:grid-cols-6"
};

export const ClipStatTiles = ({ stats }: { stats: ClipStat[] }): JSX.Element | null => {
  if (stats.length === 0) return null;
  return (
    <div className={cn("grid gap-2", TILE_GRID[Math.min(stats.length, 6)] ?? "grid-cols-3")}>
      {stats.map((stat) => {
        const tile = (
          <div
            className={cn(
              "rounded-xl border border-border bg-card/50 px-3 py-2",
              stat.hint && "cursor-help"
            )}
          >
            <p className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
              {stat.label}
            </p>
            <p className="mt-0.5 text-lg font-bold tabular-nums">{stat.value}</p>
          </div>
        );
        return stat.hint ? (
          <Tip key={stat.label} label={stat.hint} side="top" showCaret>
            {tile}
          </Tip>
        ) : (
          <div key={stat.label}>{tile}</div>
        );
      })}
    </div>
  );
};

// --- scoreboard --------------------------------------------------------------

/**
 * Column widths shared by the header and every row. Kept in one place because
 * the two must agree exactly or the header drifts off its column.
 */
const COL = {
  kda: "w-[4.75rem] shrink-0 text-center",
  cs: "w-11 shrink-0 text-center",
  gold: "hidden w-11 shrink-0 text-center sm:block",
  items: "ml-1 hidden w-[5.75rem] shrink-0 md:block"
} as const;

const ColumnHeaders = ({
  labels,
  showItems
}: {
  labels: string[];
  showItems: boolean;
}): JSX.Element => (
  <div className="flex items-center gap-2 px-2 text-[10px] font-semibold tracking-wide text-muted-foreground/80 uppercase">
    <span className="min-w-0 flex-1">Player</span>
    <span className={COL.kda}>{labels[0] ?? "KDA"}</span>
    <span className={COL.cs}>{labels[1] ?? "CS"}</span>
    <span className={COL.gold}>{labels[2] ?? "Gold"}</span>
    {showItems ? <span className={COL.items}>Items</span> : null}
  </div>
);

const PlayerRow = ({
  player,
  platformId,
  ddragonVersion,
  showItems
}: {
  player: ClipPlayerRow;
  platformId?: string | null;
  ddragonVersion?: string | null;
  showItems: boolean;
}): JSX.Element => {
  const [kdaCell, csCell, goldCell] = player.cells;
  const kdaSub = kdaRatioSub(kdaCell);
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg px-2 py-1.5",
        player.isLocal && "bg-primary/10 ring-1 ring-primary/40"
      )}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="relative shrink-0">
          <Icon
            icon={player.iconUrl ? { url: player.iconUrl, alt: player.name } : null}
            ddragonVersion={ddragonVersion}
            className="size-8"
            cropBorder
          />
          {player.iconBadge ? (
            <span className="absolute bottom-0 left-0 flex min-w-3.5 items-center justify-center rounded-sm bg-black/85 px-0.5 text-4xs font-bold text-white tabular-nums">
              {player.iconBadge}
            </span>
          ) : null}
        </span>

        {player.loadout && player.loadout.length > 0 ? (
          <span className="flex shrink-0 gap-0.5">
            {player.loadout.map((group, groupIndex) => (
              <span key={groupIndex} className="flex flex-col gap-0.5">
                {group.icons.map((entry, iconIndex) => (
                  <Icon
                    key={iconIndex}
                    icon={entry}
                    ddragonVersion={ddragonVersion}
                    className="size-3.5"
                  />
                ))}
              </span>
            ))}
          </span>
        ) : null}

        <span className="flex min-w-0 flex-1 flex-col leading-tight">
          <ClipRiotIdLabel
            name={player.name}
            tag={player.tag}
            platformId={platformId}
            isLocal={player.isLocal}
          />
          {player.detail ? (
            <span className="mt-0.5 flex min-w-0 items-center gap-1">
              {player.detailIconUrl ? (
                <img
                  src={player.detailIconUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  className="size-3 shrink-0"
                />
              ) : null}
              <span className="truncate text-[10px] text-muted-foreground">{player.detail}</span>
            </span>
          ) : null}
        </span>
      </span>

      <span className={cn(COL.kda, "text-xs leading-tight")}>
        <span className="block tabular-nums">{kdaCell?.value ?? "—"}</span>
        {kdaSub ? (
          <span className={cn("block text-[10px] font-bold tabular-nums", TONE_CLASS[kdaSub.tone])}>
            {kdaSub.text}
          </span>
        ) : null}
      </span>

      {/* CS carries its per-minute rate on hover, exactly as the app does. */}
      <Tip label={csCell?.hint ?? ""} side="top" disabled={!csCell?.hint} showCaret>
        <span
          className={cn(
            COL.cs,
            "text-[11px] text-muted-foreground tabular-nums",
            csCell?.hint && "cursor-help"
          )}
        >
          {csCell?.value ?? "—"}
        </span>
      </Tip>
      <span className={cn(COL.gold, "text-[11px] text-muted-foreground tabular-nums")}>
        {goldCell?.value ?? "—"}
      </span>

      {showItems ? (
        <span className={COL.items}>
          <span className="grid shrink-0 grid-cols-4 gap-1">
            {(player.items ?? []).map((entry, index) => (
              <Icon
                key={index}
                icon={entry}
                ddragonVersion={ddragonVersion}
                className="size-5"
                rounded="rounded-[3px]"
              />
            ))}
          </span>
        </span>
      ) : null}
    </div>
  );
};

const TeamBoard = ({
  team,
  platformId,
  ddragonVersion
}: {
  team: ClipTeam;
  platformId?: string | null;
  ddragonVersion?: string | null;
}): JSX.Element => {
  const labels = team.players[0]?.cells.map((cell) => cell.label) ?? [];
  const outcome = team.won === null || team.won === undefined ? null : team.won;
  // Games without a build (TFT, Valorant) must not leave an empty Items column.
  const showItems = team.players.some((player) => (player.items?.length ?? 0) > 0);

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-baseline justify-between gap-3 px-2">
        <span
          className={cn(
            "text-xs font-black uppercase",
            outcome === null ? "text-muted-foreground" : outcome ? "text-ally" : "text-enemy"
          )}
        >
          {outcome === null ? "Result" : outcome ? "Victory" : "Defeat"}{" "}
          <span className="font-semibold opacity-70">({team.name})</span>
        </span>
        {team.summary ? (
          <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
            {team.summary}
          </span>
        ) : null}
      </div>

      <ColumnHeaders labels={labels} showItems={showItems} />
      <div className="flex flex-col gap-0.5">
        {team.players.map((player, index) => (
          <PlayerRow
            key={`${player.name}-${index}`}
            player={player}
            platformId={platformId}
            ddragonVersion={ddragonVersion}
            showItems={showItems}
          />
        ))}
      </div>
    </div>
  );
};

export const ClipScoreboard = ({
  teams,
  platformId,
  ddragonVersion
}: {
  teams: ClipTeam[];
  platformId?: string | null;
  ddragonVersion?: string | null;
}): JSX.Element | null => {
  const populated = teams.filter((team) => team.players.length > 0);
  if (populated.length === 0) return null;
  return (
    <div className="flex flex-col gap-4">
      {populated.map((team, index) => (
        <TeamBoard
          key={`${team.name}-${index}`}
          team={team}
          platformId={platformId}
          ddragonVersion={ddragonVersion}
        />
      ))}
    </div>
  );
};

/** Full match details for a clip, or nothing when the sidecar carries none. */
export const ClipMatchStats = ({ match }: { match: ClipMatchSummary }): JSX.Element | null => {
  const stats = match.stats ?? [];
  const teams = match.teams ?? [];
  if (stats.length === 0 && teams.length === 0) return null;

  return (
    <div className="flex flex-col gap-5">
      <ClipStatTiles stats={stats} />
      <ClipScoreboard
        teams={teams}
        platformId={match.platformId}
        ddragonVersion={match.ddragonVersion}
      />
    </div>
  );
};
